import {
  WSAlert,
  WSElement,
  WSList,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import React, { useMemo, useState } from "react";
import {
  GeneralRequirementType,
  IPayeeResponse,
  PayerPayeeStatus,
  RequirementStatus
} from "@wingspanhq/payments/dist/interfaces";
import {
  RequirementContextType,
  RequirementTableItemData
} from "../../../Requirements/types";
import { useUserId } from "../../../../query/hooks/helpers";
import { useFocusedFeatures } from "../../../../FocusedFeatures";
import { useHistory } from "react-router-dom";
import { ExtendedRequirementsTable } from "../../../Requirements/components/ExtendedRequirementsTable";
import { ExtendedRequirementPreviewList } from "../../../Requirements/components/ExtendedRequirementPreviewList";
import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces";
import { ExtendedRequirementItem } from "../../../Requirements/modules/GeneralRequirements/types";

export type EligibilityPanelProps = {
  payee: IPayeeResponse;
  noActions?: boolean;
  hasEligibleEngagements: boolean;
  requirements: IRequirementResponse[];
};

export const EligibilityRequirements: React.FC<EligibilityPanelProps> = ({
  hasEligibleEngagements,
  requirements,
  payee,
  noActions
}) => {
  const history = useHistory();
  const userId = useUserId();
  const focusedFeatures = useFocusedFeatures();

  const generalRequirements = useMemo(() => {
    return (
      payee.requirements?.filter(requirement =>
        focusedFeatures.showContractorsEligibility
          ? true
          : requirement.requirementType !== GeneralRequirementType.PayoutMethod
      ) || []
    );
  }, [payee.requirements]);

  const allRequirements = useMemo(() => {
    return [...generalRequirements, ...requirements];
  }, [generalRequirements, requirements]);

  const completedLength = useMemo(() => {
    return allRequirements.filter(e => e.status === RequirementStatus.Complete)
      .length;
  }, [allRequirements]);

  const isRequirementsComplete = useMemo(() => {
    return allRequirements.every(e => e.status === RequirementStatus.Complete);
  }, [allRequirements]);

  const [showPreviews, setShowPreviews] = useState(false);

  const getOnViewDetails = (requirement: ExtendedRequirementItem) => {
    return "requirementId" in requirement
      ? (requirement: IRequirementResponse) =>
          history.push(
            `/member/payees/${payee.payeeId}/requirement/${requirement.requirementId}`
          )
      : undefined;
  };

  return (
    <WSElement>
      <WSList gap="L">
        <WSSectionToolbar
          title="Requirements"
          pill={
            payee.payerOwnedData.status === PayerPayeeStatus.Inactive
              ? {
                  icon: "archive",
                  text: "Archived",
                  theme: "neutral"
                }
              : {
                  icon: true,
                  text: `${completedLength} of ${allRequirements.length} completed`,
                  theme:
                    isRequirementsComplete && hasEligibleEngagements
                      ? "success"
                      : "warning"
                }
          }
          buttons={[
            {
              label: "",
              onClick: () => setShowPreviews(true),
              kind: showPreviews ? "Secondary" : "Link",
              icon: "grid",
              onlyIcon: true,
              size: "S"
            },
            {
              label: "",
              onClick: () => setShowPreviews(false),
              kind: !showPreviews ? "Secondary" : "Link",
              icon: "list",
              onlyIcon: true,
              size: "S"
            }
          ]}
        />

        {showPreviews ? (
          <ExtendedRequirementPreviewList
            requirements={allRequirements}
            payeeId={payee.payeeId}
            payee={payee}
            payerId={userId}
            contextType={RequirementContextType.Payee}
            getOnViewDetails={getOnViewDetails}
          />
        ) : (
          <ExtendedRequirementsTable
            payeeId={payee.payeeId}
            payerId={userId}
            payee={payee}
            requirements={allRequirements}
            contextType={RequirementContextType.Payee}
            showOverrideActions
            hideActions={noActions}
            getOnViewDetails={getOnViewDetails}
          />
        )}
      </WSList>
    </WSElement>
  );
};
